/**
 * Generador de código ZPL (Zebra Programming Language) para impresoras térmicas
 * Optimizado específicamente para Rongta RP425 (203 DPI / 8 puntos por mm)
 */

export const LABEL_PRESETS = {
  '50x25': { name: '50 x 25 mm (Estándar Precio)', widthMm: 50, heightMm: 25, widthDots: 400, heightDots: 200 },
  '50x30': { name: '50 x 30 mm (Retail Mediano)', widthMm: 50, heightMm: 30, widthDots: 400, heightDots: 240 },
  '40x30': { name: '40 x 30 mm (Compacto)', widthMm: 40, heightMm: 30, widthDots: 320, heightDots: 240 },
  '38x25': { name: '38 x 25 mm (Joyería / Accesorios)', widthMm: 38, heightMm: 25, widthDots: 304, heightDots: 200 },
  '30x20': { name: '30 x 20 mm (Mini)', widthMm: 30, heightMm: 20, widthDots: 240, heightDots: 160 },
  '76x50': { name: '3" x 2" (76 x 50 mm)', widthMm: 76, heightMm: 50, widthDots: 608, heightDots: 400 },
  '100x50': { name: '4" x 2" (100 x 50 mm)', widthMm: 100, heightMm: 50, widthDots: 800, heightDots: 400 },
  '100x75': { name: '4" x 3" (100 x 75 mm)', widthMm: 100, heightMm: 75, widthDots: 800, heightDots: 600 },
  '100x150': { name: '4" x 6" (100 x 150 mm Envíos)', widthMm: 100, heightMm: 150, widthDots: 812, heightDots: 1218 },
  '58mm_roll': { name: 'Rollo Continuo 58 mm', widthMm: 58, heightMm: 80, widthDots: 464, heightDots: 640 },
};

/**
 * Limpia y normaliza texto para ZPL (ASCII)
 */
function sanitizeZplText(text = '') {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Quitar tildes
    .replace(/[^\x20-\x7E]/g, '') // Solo caracteres ASCII imprimibles
    .replace(/[\^~]/g, ''); // Evitar caracteres de control ZPL
}

/**
 * Genera ZPL para etiqueta de producto / precio
 * @param {Object} product - Datos del producto (name, code, price, category, etc.)
 * @param {Object} options - Opciones de configuración
 * @returns {string} Código ZPL listo para enviar a la impresora
 */
export function generateZplLabel(product, options = {}) {
  const {
    size = '50x25',
    copies = 1,
    type = 'barcode', // 'barcode' | 'qr'
    showPrice = true,
    showName = true,
    showCode = true,
    showCategory = false,
    showBusinessName = false,
    businessName = 'POSENT',
    currencySymbol = '$',
    darkness = 26, // Máxima nitidez y oscuridad térmica para RP425
  } = options;

  const preset = LABEL_PRESETS[size] || LABEL_PRESETS['50x25'];
  const widthDots = preset.widthDots;
  const heightDots = preset.heightDots;

  const name = sanitizeZplText(product?.name || 'PRODUCTO').substring(0, 26);
  const code = sanitizeZplText(
    product?.barcode ||
    product?.codigo_barras ||
    product?.code ||
    product?.codigo ||
    product?.sku ||
    (product?._id || product?.id ? `PRD-${String(product._id || product.id).slice(-8).toUpperCase()}` : '000000')
  );
  const priceVal = typeof product?.price === 'number' ? product.price.toFixed(2) : sanitizeZplText(product?.price || '0.00');
  const category = sanitizeZplText(product?.category || '').substring(0, 18);
  const biz = sanitizeZplText(businessName).substring(0, 20);

  let zpl = '^XA\n';
  zpl += `^PW${widthDots}\n`;
  zpl += `^LL${heightDots}\n`;
  zpl += '^LH0,0\n';
  zpl += `^MD${darkness}\n`; // Oscuridad térmica
  zpl += '^PR3,3\n'; // Velocidad óptima para máxima adherencia y oscuridad

  let currentY = 12;

  // 1. Nombre del Negocio (Opcional)
  if (showBusinessName && biz) {
    zpl += `^FO10,${currentY}^A0N,20,20^FB${widthDots - 20},1,0,C^FD${biz}^FS\n`;
    currentY += 24;
  }

  // 2. Nombre del Producto (Negrita destacada)
  if (showName) {
    const fontH = heightDots <= 200 ? 26 : 30;
    const fontW = heightDots <= 200 ? 24 : 28;
    zpl += `^FO10,${currentY}^A0N,${fontH},${fontW}^FB${widthDots - 20},1,0,C^FD${name}^FS\n`;
    currentY += fontH + 6;
  }

  // 3. Código de Barras o Código QR (Barras gruesas y nítidas)
  if (showCode && code) {
    if (type === 'qr') {
      const qrMag = heightDots <= 200 ? 4 : 5;
      const qrX = Math.max(10, Math.floor((widthDots - (qrMag * 25)) / 2));
      zpl += `^FO${qrX},${currentY}^BQN,2,${qrMag},Q^FDLA,${code}^FS\n`;
      currentY += (qrMag * 24) + 6;
    } else {
      // Barcode Code128 con ratio grueso y centrado seguro sin desbordamiento
      const barH = heightDots <= 200 ? 52 : 65;
      const barRatio = (code.length > 9 || widthDots <= 400) ? 2 : 3;
      const estBarWidth = (code.length * 11 + 35) * barRatio;
      const barX = Math.max(12, Math.floor((widthDots - estBarWidth) / 2));
      zpl += `^FO${barX},${currentY}^BY${barRatio},3,${barH}^BCN,${barH},Y,N,N^FD${code}^FS\n`;
      currentY += barH + 24;
    }
  }

  // 4. Categoría (Opcional)
  if (showCategory && category) {
    zpl += `^FO10,${currentY}^A0N,16,16^FB${widthDots - 20},1,0,C^FD${category}^FS\n`;
    currentY += 18;
  }

  // 5. Precio
  if (showPrice) {
    const priceH = heightDots <= 200 ? 32 : 38;
    const priceW = heightDots <= 200 ? 28 : 34;
    const formattedPrice = `${currencySymbol}${priceVal}`;
    zpl += `^FO10,${currentY}^A0N,${priceH},${priceW}^FB${widthDots - 20},1,0,C^FD${formattedPrice}^FS\n`;
  }

  // Cantidad de copias
  zpl += `^PQ${Math.max(1, copies)}\n`;
  zpl += '^XZ\n';

  return zpl;
}

/**
 * Genera ZPL para ticket / recibo de venta
 * @param {Object} sale - Datos de la venta
 * @param {Object} options - Opciones de configuración
 * @returns {string} Código ZPL del ticket
 */
export function generateZplTicket(sale, options = {}) {
  const {
    widthMm = 58,
    copies = 1,
    businessName = 'POSENT POS',
    businessPhone = '',
    businessAddress = '',
    currencySymbol = '$',
  } = options;

  const widthDots = widthMm === 80 ? 640 : 464;
  const items = sale.items || [];
  const total = typeof sale.total === 'number' ? sale.total.toFixed(2) : sanitizeZplText(sale.total || '0.00');
  const subtotal = typeof sale.subtotal === 'number' ? sale.subtotal.toFixed(2) : sanitizeZplText(sale.subtotal || total);
  const ticketNumber = sanitizeZplText(sale.ticketNumber || sale.numero_venta || sale.invoice_number || 'TKT-001');

  // Estimar largo dinámico (incluyendo código de barras al final)
  const estimatedHeight = 440 + (items.length * 40);

  let zpl = '^XA\n';
  zpl += `^PW${widthDots}\n`;
  zpl += `^LL${estimatedHeight}\n`;
  zpl += '^LH0,0\n';
  zpl += '^MD15\n';

  let y = 15;

  // Cabecera
  zpl += `^FO10,${y}^A0N,28,26^FB${widthDots - 20},1,0,C^FD${sanitizeZplText(businessName)}^FS\n`;
  y += 32;

  if (businessAddress) {
    zpl += `^FO10,${y}^A0N,18,18^FB${widthDots - 20},1,0,C^FD${sanitizeZplText(businessAddress)}^FS\n`;
    y += 22;
  }
  if (businessPhone) {
    zpl += `^FO10,${y}^A0N,18,18^FB${widthDots - 20},1,0,C^FDTel: ${sanitizeZplText(businessPhone)}^FS\n`;
    y += 22;
  }

  // Separador
  zpl += `^FO10,${y}^GB${widthDots - 20},2,2^FS\n`;
  y += 10;

  // Ticket info
  zpl += `^FO10,${y}^A0N,20,20^FDDoc: ${ticketNumber}^FS\n`;
  y += 24;
  zpl += `^FO10,${y}^A0N,18,18^FDFecha: ${new Date().toLocaleDateString()}^FS\n`;
  y += 24;

  // Separador
  zpl += `^FO10,${y}^GB${widthDots - 20},2,2^FS\n`;
  y += 10;

  // Items
  items.forEach((item) => {
    const itemName = sanitizeZplText(item.name || item.nombre || 'Item').substring(0, 24);
    const qty = item.quantity || item.cantidad || 1;
    const price = typeof item.price === 'number' ? item.price.toFixed(2) : (item.unit_price || '0.00');
    const sub = typeof item.subtotal === 'number' ? item.subtotal.toFixed(2) : (qty * price).toFixed(2);

    zpl += `^FO10,${y}^A0N,18,18^FD${itemName}^FS\n`;
    y += 20;
    zpl += `^FO20,${y}^A0N,18,18^FD${qty} x ${currencySymbol}${price}^FS\n`;
    zpl += `^FO${widthDots - 130},${y}^A0N,18,18^FD${currencySymbol}${sub}^FS\n`;
    y += 24;
  });

  // Separador
  zpl += `^FO10,${y}^GB${widthDots - 20},2,2^FS\n`;
  y += 12;

  // Totales
  zpl += `^FO${widthDots - 220},${y}^A0N,20,20^FDSubtotal: ${currencySymbol}${subtotal}^FS\n`;
  y += 26;
  zpl += `^FO${widthDots - 220},${y}^A0N,26,24^FDTOTAL: ${currencySymbol}${total}^FS\n`;
  y += 34;

  // Separador
  zpl += `^FO10,${y}^GB${widthDots - 20},2,2^FS\n`;
  y += 12;

  // Código de barras del ticket para lector físico de barras
  const barH = 48;
  const barRatio = widthDots >= 600 ? 3 : 2;
  const estBarWidth = (ticketNumber.length * 11 + 35) * barRatio;
  const barX = Math.max(10, Math.floor((widthDots - estBarWidth) / 2));
  zpl += `^FO${barX},${y}^BY${barRatio},3,${barH}^BCN,${barH},Y,N,N^FD${ticketNumber}^FS\n`;
  y += barH + 26;

  // Footer
  zpl += `^FO10,${y}^A0N,18,18^FB${widthDots - 20},1,0,C^FDGracias por su compra^FS\n`;
  y += 24;
  zpl += `^FO10,${y}^A0N,14,14^FB${widthDots - 20},1,0,C^FDPOSENT PRO^FS\n`;

  zpl += `^PQ${Math.max(1, copies)}\n`;
  zpl += '^XZ\n';

  return zpl;
}

/**
 * Descarga una cadena ZPL como archivo .zpl para imprimir o enviar mediante utilidades
 */
export function downloadZplFile(zplContent, fileName = 'etiqueta.zpl') {
  const blob = new Blob([zplContent], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
